/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

// libraries
import React, {
    useEffect, useRef,
} from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter, Redirect } from 'react-router';
import { compose } from 'recompose';
import { useLazyQuery } from 'react-apollo';
import gql from 'graphql-tag';
import withStyles from '@material-ui/core/styles/withStyles';
import {
    object, string, shape, bool, arrayOf, number, array, func,
} from 'prop-types';
import mbpLogger from 'mbp-logger';

// product container ui components
import SimpleProductSkeleton from '../../../GraphqlSkeletonComponents/SimpleProductSkeleton';
import GraphqlProductContainerBody from './GraphqlProductContainerBody';
import GraphqlBestSellerCollection from '../../../Graphql404Error/Graphql404ErrorProductContainer';

// helpers, redux, hooks
import { GRAPHQL_ENV } from '../../../../../gql';
import {
    getUserSubmittedProductFilterZipcode,
    getLocationType,
    getAddressSource,
    getDataDictionary,
    getCurrencyData,
} from '../../../../../../state/ducks/App/App-Selectors';
import {
    setUserSubmittedProductFilterZipcode,
    setLocationType,
} from '../../../../../../state/ducks/App/App-Actions';
import { getBrand } from '../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getBestSellersId, getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { usePrev } from '../../../../../helpers/hooks/usePrev';
import { determineCollectionPrefixPath } from '../../../../../helpers/determineInternationalizationPath';
import { setLocale } from '../../../../../../state/ducks/Member/ducks/Auth/helper/helper';
import priceRangeFilter from '../../../../../helpers/priceRangeFilter';

const styles = () => ({
    noProductsText: {
        color: '#b50000',
        margin: '16px 0',
        fontSize: '14px',
        textAlign: 'center',
        fontWeight: 'bold',
    },
});
const CATEGORY_PRODUCT_QUERY = gql`
query CategoryProductQuery($productOptions: ProductOptions, $brand: String!, $environment: String!, $id: String!, $locale: String!, $zipCode: String!, $locationType: String!, $currency: String!) {
    findCategoryById(brand: $brand, environment: $environment, id: $id, locale: $locale, productOptions: $productOptions, zipCode: $zipCode, locationType: $locationType, currency: $currency) {
        products {
            id
            brandId
            is3dEnabled
        image {
            additionalImages
            altText
            name
            path
            snipe
            snipeImagePath
            snipeProductEndDate
            snipeCategoryEndDate
            snipeProductStartDate
            snipeCategoryStartDate
            collectionImage
            __typename
        }
        productType
        reviews
        partNumber
        baseCode
        prices {
            currency
            type
            value
            __typename
        }
        skuPriceRange {
            sale {
                value
            }
            retail {
                value
            }
            __typename
        }
        name
        brand
        seo {
            url
            __typename

        }
        productSkus {
            marketplaceShopName
            partNumber
            id
            __typename
          }
        isPassportEligible
        isPersonalizable
        availability {
            availabilityIndicator
            deliveryDateType
            displayEndDate
            displayStartDate
            earliestShipDate
            latestShipDate
            perishable
            productDeliveryType
            shipAlone
            deliveryMessage
            __typename
        }
        __typename
    }
    totalProducts
    __typename
}
}
`;

const defaultPageSize = 24;

const determineProductOptions = ({ lowToHighBtn, highToLowBtn }, facetsData, priceRangeData, zipCode, locationType, pageNumber, isBot, categoryData, activeABTests, pageSize = defaultPageSize, isAltProductRankEnabled, isFacetFilterPriceEnabled) => {
    const modelName = '';
    const orderBy = 'BEST_SELLERS';
    let productOptions = {
        orderBy: 'BEST_SELLERS',
        locationType,
        page: pageNumber,
        pageSize,
    };
    if (zipCode !== '' && !facetsData.length) {
        if (highToLowBtn) {
            return {
                orderBy: 'PRICE_DESC', zipCode, locationType, page: pageNumber, pageSize,
            };
        }
        if (lowToHighBtn) {
            return {
                orderBy: 'PRICE_ASC', zipCode, locationType, page: pageNumber, pageSize,
            };
        }
        return {
            orderBy, modelName, zipCode, locationType, page: pageNumber, pageSize,
        };
    }
    if (highToLowBtn) {
        productOptions = {
            orderBy: 'PRICE_DESC', locationType, page: pageNumber, pageSize,
        };
    }
    if (lowToHighBtn) {
        productOptions = {
            orderBy: 'PRICE_ASC', locationType, page: pageNumber, pageSize,
        };
    }

    if (facetsData.length) {
        productOptions = {
            page: pageNumber, pageSize, orderBy: productOptions.orderBy,
        };
        let priceRanges = [];
        let newFacetsData = facetsData;
        if (isFacetFilterPriceEnabled) {
            // first we checked that value is present or not
            if (typeof priceRangeData !== 'undefined' && priceRangeData?.entries?.length > 0) {
                newFacetsData = [];
                const priceRangeFilterData = priceRangeFilter(facetsData, priceRangeData);
                newFacetsData = priceRangeFilterData.newFacetsData;
                priceRanges = priceRangeFilterData.priceRanges;
            }
            if (priceRanges.length > 0) {
                productOptions.priceRangeFilter = {
                    fieldName: 'all_prices',
                    ranges: priceRanges,
                };
            }
        }
        if (newFacetsData.length > 0) {
            productOptions.facets = newFacetsData;
        }
    }
    return productOptions;
};

const GraphqlProductContainer = ({
    brand, classes, categoryData, userSubmittedZip, locationType, breadCrumbArray, filterData: {
        giftBox, localFlorist, lowToHighBtn, highToLowBtn, sellingBtn,
    }, path, skipAddons, initialProductData, filterData, facetsData, addressSource, location, setFilterZip, setLocation, bestSellersId, showMovieUpsell, showLeftRailContent,
    dataDictionary, currencyData, SCICollectionId, subCollectionProducts, productDisplayCount, sciMainCollectionLabel, cmsShowSubcollection, priceRangeData, mobileGridLayout, pullProductCount,
    isSmallScreen,
}) => {
    const isAltProductRankEnabled = false;
    const isFacetFilterPriceEnabled = useSelector(getFeatureFlag('is-facet-filter-price-enabled'));

    const pageCount = useRef(2);
    const filterPropChanged = useRef(false);
    const prevPath = usePrev(path);
    const isBot = false;
    const activeABTests = false;
    const productsToLoad = 24;
    const [getProducts, {
        loading, error, data, fetchMore,
    }] = useLazyQuery(CATEGORY_PRODUCT_QUERY, {
        variables: {
            productOptions: determineProductOptions(filterData, facetsData, priceRangeData, userSubmittedZip, locationType, 1, isBot, categoryData, activeABTests, productsToLoad, isAltProductRankEnabled, isFacetFilterPriceEnabled),
            brand: brand.domain,
            environment: GRAPHQL_ENV,
            id: categoryData?.category?.id,
            locale: 'en-us',
            zipCode: userSubmittedZip,
            locationType,
        },
    });
    const isMounted = useRef(true); // see useEffect comment for explination of this useRef
    useEffect(() => {
        // useEffect normally runs on mount, unmount, and when any values given in the array param are modified
        // this query should not run on component mount. Only if one of the array values is modified
        // Setting a ref to false initiailly and only executing the query if the ref returns true
        // Ensures that the query will not run on mount when useEffect is first triggered
        if (isMounted.current) {
            // This query only runs if a filter prop is changed
            pageCount.current = 1;
            filterPropChanged.current = true;
            getProducts({
                variables: {
                    productOptions: determineProductOptions(filterData, facetsData, priceRangeData, userSubmittedZip, locationType, pageCount.current, isBot, categoryData, activeABTests, productsToLoad, isAltProductRankEnabled, isFacetFilterPriceEnabled),
                    brand: brand.domain,
                    environment: GRAPHQL_ENV,
                    id: SCICollectionId || categoryData?.category?.id,
                    locale: setLocale(),
                    currency: currencyData?.currencyName,
                },
            });
            pullProductCount(data?.findCategoryById?.totalProducts);
        } else {
            isMounted.current = true;
        }
    }, [locationType, userSubmittedZip, lowToHighBtn, highToLowBtn, sellingBtn, facetsData?.length]);
    useEffect(() => {
        // this useEffect is to prevent a bug where the user goes to a category page and paginates
        // then goes to a different category page and paginates
        // then goes back to the first page.
        // Before this fix users would see the second page products when they returned to the first page.
        // This was because the results of findCategoryByUrl (in GraphqlCategoryBuilder)
        // were cached when returning to the first page, and so there was no loading state in cateogrybuilder
        // and so this component never unmounts. Because it never unmounts the data returned from the lazy query on the second page still existed
        // and was displaying instead of the first page products.
        // the fix is to run this query automatically if going to a category page the user has already visited
        // there is no performance degredation because the results of this query will already be cached.
        if (path !== prevPath && prevPath !== undefined) {
            pageCount.current = 1;
            getProducts({
                variables: {
                    productOptions: determineProductOptions(filterData, facetsData, priceRangeData, userSubmittedZip, locationType, pageCount.current, isBot, categoryData, activeABTests, productsToLoad, isAltProductRankEnabled, isFacetFilterPriceEnabled),
                    brand: brand.domain,
                    environment: GRAPHQL_ENV,
                    id: SCICollectionId || categoryData?.category?.id,
                    locale: setLocale(),
                    currency: currencyData?.currencyName,
                },
            });
            pullProductCount(data?.findCategoryById?.totalProducts);
        }
    }, [path]);
    const determineQuery = () => {
        // this fuction is used to determine which query to run when a user scrolls to the end of currently loaded products
        // fetchMore is a function returned from the useLazyQuery hook.
        // Since the the query in this container is not run by default on cat page load the fetchMore function might not exist initially
        // If it does, begin the pagination process
        // if not, run the query for the first time

        if (fetchMore && !productDisplayCount) {
            if (data?.findCategoryById?.products?.length) {
                pageCount.current = Math.ceil(data?.findCategoryById?.products?.length / defaultPageSize) + 1;
            } else {
                pageCount.current += 1;
            }
            fetchMore({
                variables: {
                    productOptions: determineProductOptions(filterData, facetsData, priceRangeData, userSubmittedZip, locationType, pageCount.current, isBot, categoryData, activeABTests, productsToLoad, isAltProductRankEnabled, isFacetFilterPriceEnabled),
                    brand: brand.domain,
                    environment: GRAPHQL_ENV,
                    id: SCICollectionId || categoryData?.category?.id,
                    locale: 'en-us',
                },
                updateQuery: (prev, { fetchMoreResult }) => {
                    if (!fetchMoreResult) return prev;
                    const prevProductFeed = prev?.findCategoryById;
                    const nextProductFeed = fetchMoreResult?.findCategoryById;
                    return {
                        ...prev,
                        findCategoryById: {
                            products: [
                                ...prevProductFeed.products,
                                ...nextProductFeed.products,
                            ],
                            /* eslint-disable-next-line no-underscore-dangle */
                            __typename: fetchMoreResult?.findCategoryById?.__typename,
                        },
                    };
                },
            });
            pullProductCount(data?.findCategoryById?.totalProducts);
        } else {
            // if this conditional section is activated it means the user is trying to load the second page of products
            // the pageCount is set to one, but to prevent only returning the products already on the page
            // determineProductOptions is called with an optional flag that causes the number of products returned to double
            // If a normal page size is 12 products, this will return 24.
            if (!productDisplayCount) {
                pageCount.current = 1;
                getProducts({
                    variables: {
                        productOptions: determineProductOptions(filterData, facetsData, priceRangeData, userSubmittedZip, locationType, pageCount.current, isBot, categoryData, activeABTests, productsToLoad, isAltProductRankEnabled, isFacetFilterPriceEnabled),
                        brand: brand.domain,
                        environment: GRAPHQL_ENV,
                        id: SCICollectionId || categoryData?.category?.id,
                        locale: setLocale(),
                        currency: currencyData?.currencyName,
                    },
                });
                // the pageCount is set to two and then is incrememnted by 1 below to end up at 3.
                // when the user scrolls to the bottom of the products the next query will return the normal number of products
                // and begin from page 3
                pageCount.current = 2;
                pullProductCount(data?.findCategoryById?.totalProducts);
            }
            pageCount.current += 1;
        }
    };
    if (data) {
        pullProductCount(data?.findCategoryById?.totalProducts);
    }
    if (loading && !data && initialProductData && !filterPropChanged.current) {
        return (
            <GraphqlProductContainerBody
                data={initialProductData}
                categoryData={categoryData}
                path={path}
                location={location}
                breadCrumbArray={breadCrumbArray}
                filterData={{ giftBox, localFlorist, options: { lowToHighBtn, highToLowBtn, sellingBtn } }}
                skipAddons={skipAddons}
                showMovieUpsell={showMovieUpsell}
                onLoadMore={determineQuery}
                pageNumber={pageCount.current}
                loader
                showLeftRailContent={showLeftRailContent}
                productDisplayCount={productDisplayCount}
                subCollectionProducts={subCollectionProducts}
                sciMainCollectionLabel={sciMainCollectionLabel}
                cmsShowSubcollection={cmsShowSubcollection}
                mobileGridLayout={mobileGridLayout}
                pullProductCount={pullProductCount}
                isSmallScreen={isSmallScreen}
            />
        );
    }
    if (loading) {
        return <SimpleProductSkeleton />;
    }

    if (error) {
        mbpLogger.logError({
            query: CATEGORY_PRODUCT_QUERY,
            component: 'GraphqlProductContainer.js',
            message: 'Error loading data from Graphql',
            env: GRAPHQL_ENV,
            error,
        });
        return null;
    }
    /**
     * @description Products not available for current zip
     * - SCI redirects to default sympathy collection
     * - All other flows and channels display and message and best sellers collection
     */
    if (!data) {
        if (initialProductData) {
            if (initialProductData.length < 1) {
                // SCI channel redirect
                if (addressSource === 'SCI') {
                    const cuurentZip = userSubmittedZip;
                    setFilterZip(cuurentZip);
                    setLocation('Residence');
                    // redirect to /sympathy-flowers-canada
                    return <Redirect to={`${determineCollectionPrefixPath('/sympathy-flowers-canada', false)}${location.search}`} />;
                }
                // All other flows and channels
                return (
                    <>
                        <div className={classes.noProductsText}>
                            {dataDictionary?.['There are currently no products available in this collection'] || 'There are currently no products available in this collection'}. {dataDictionary?.['Try your search again using the search box at the top of the page, or select from our best sellers below'] || 'Try your search again using the search box at the top of the page, or select from our best sellers below'}.
                        </div>
                        <GraphqlBestSellerCollection brand={brand} categoryCode={bestSellersId} message={dataDictionary?.['Here are some of our best sellers']} />
                    </>
                );
            }
            return (
                <GraphqlProductContainerBody
                    data={initialProductData}
                    categoryData={categoryData}
                    path={path}
                    location={location}
                    breadCrumbArray={breadCrumbArray}
                    filterData={{ giftBox, localFlorist, options: { lowToHighBtn, highToLowBtn, sellingBtn } }}
                    skipAddons={skipAddons}
                    showMovieUpsell={showMovieUpsell}
                    onLoadMore={determineQuery}
                    pageNumber={pageCount.current}
                    loader={false}
                    showLeftRailContent={showLeftRailContent}
                    productDisplayCount={productDisplayCount}
                    subCollectionProducts={subCollectionProducts}
                    sciMainCollectionLabel={sciMainCollectionLabel}
                    cmsShowSubcollection={cmsShowSubcollection}
                    pullProductCount={pullProductCount}
                    isSmallScreen={isSmallScreen}
                />
            );
        }
        mbpLogger.logWarning({
            query: CATEGORY_PRODUCT_QUERY,
            component: 'GraphqlProductContainer.js',
            message: 'No data returned for query',
            env: GRAPHQL_ENV,
        });

        return null;
    }
    return (
        <>
            {data?.findCategoryById?.products?.length < 1
                ? (
                    <>
                        <div className={classes.noProductsText}>There are currently no products available in this collection. Try your search again using the search box at the top of the page, or select from our best sellers below.</div>
                        <GraphqlBestSellerCollection brand={brand} categoryCode={bestSellersId} message="Here are some of our best sellers..." />
                    </>
                ) : (
                    <GraphqlProductContainerBody
                        data={data?.findCategoryById?.products || []}
                        categoryData={categoryData}
                        path={path}
                        location={location}
                        breadCrumbArray={breadCrumbArray}
                        skipAddons={skipAddons}
                        showMovieUpsell={showMovieUpsell}
                        filterData={{ giftBox, localFlorist, options: { lowToHighBtn, highToLowBtn, sellingBtn } }}
                        pageNumber={pageCount.current}
                        onLoadMore={determineQuery}
                        loader={false}
                        showLeftRailContent={showLeftRailContent}
                        productDisplayCount={productDisplayCount}
                        subCollectionProducts={subCollectionProducts}
                        sciMainCollectionLabel={sciMainCollectionLabel}
                        cmsShowSubcollection={cmsShowSubcollection}
                        mobileGridLayout={mobileGridLayout}
                        isSmallScreen={isSmallScreen}
                    />
                )}
        </>
    );
};

GraphqlProductContainer.propTypes = {
    classes: object.isRequired,
    categoryData: object.isRequired,
    brand: object.isRequired,
    locationType: string.isRequired,
    userSubmittedZip: string.isRequired,
    breadCrumbArray: arrayOf(
        shape({
            title: string.isRequired,
            href: string.isRequired,
        }),
    ).isRequired,
    filterData: shape({
        lowToHighBtn: bool,
        highToLowBtn: bool,
        sellingBtn: bool,
        giftBox: bool,
        localFlorist: bool,
        facets: array,
    }),
    facetsData: array,
    path: string.isRequired,
    skipAddons: bool.isRequired,
    showMovieUpsell: bool.isRequired,
    initialProductData: arrayOf(
        shape({
            partNumber: string.isRequired,
            brand: string.isRequired,
            prices: arrayOf(
                shape({
                    __typename: string.isRequired,
                    currency: string.isRequired,
                    type: string.isRequired,
                    value: number.isRequired,
                }).isRequired,
            ).isRequired,
            skuPriceRange: shape({
                sale: arrayOf(
                    shape({
                        value: number.isRequired,
                    }),
                ),
                retail: arrayOf(
                    shape({
                        value: number.isRequired,
                    }),
                ),
            }).isRequired,
            availability: shape({
                deliveryMessage: string.isRequired,
            }).isRequired,
        }).isRequired,
    ),
    addressSource: string,
    location: object.isRequired,
    setFilterZip: func.isRequired,
    setLocation: func.isRequired,
    bestSellersId: string,
    showLeftRailContent: bool.isRequired,
    dataDictionary: object,
    currencyData: shape({
        currencyName: string,
    }).isRequired,
    SCICollectionId: string.isRequired,
    subCollectionProducts: array.isRequired,
    productDisplayCount: string.isRequired,
    sciMainCollectionLabel: string.isRequired,
    cmsShowSubcollection: bool.isRequired,
    priceRangeData: arrayOf(
        shape({
            to: string.isRequired,
            name: string.isRequired,
            from: string.isRequired,
        }),
    ),
    mobileGridLayout: bool.isRequired,
    pullProductCount: func.isRequired,
    isSmallScreen: bool.isRequired,
};

GraphqlProductContainer.defaultProps = {
    filterData: {},
    initialProductData: [],
    facetsData: [],
    addressSource: '',
    priceRangeData: [],
    bestSellersId: '18F-Best Selling Flowers-400215581',
    dataDictionary: {},
};

const mapStateToProps = (state) => ({
    brand: getBrand(state),
    locationType: getLocationType(state),
    userSubmittedZip: getUserSubmittedProductFilterZipcode(state),
    addressSource: getAddressSource(state),
    bestSellersId: getBestSellersId(state),
    dataDictionary: getDataDictionary(state),
    currencyData: getCurrencyData(state),
});

const mapDispatchToProps = (dispatch) => ({
    setFilterZip: bindActionCreators(
        setUserSubmittedProductFilterZipcode,
        dispatch,
    ),
    setLocation: bindActionCreators(setLocationType, dispatch),
});

const enhance = compose(
    withStyles(styles),
    withRouter,
    connect(
        mapStateToProps,
        mapDispatchToProps,
    ),
);

export default enhance(GraphqlProductContainer);
